import { ValidationError } from './errors.js';
import type { NutritionBasis, SourceType, TagDisplayCase } from './types.js';

export const MAX_TITLE_LENGTH = 300;
export const MAX_TAG_LENGTH = 60;
export const MAX_TAGS_PER_RECIPE = 50;
export const MAX_TEXT_FIELD_LENGTH = 100_000;
export const MAX_DAY_NOTE_LENGTH = 200;

export function normalizeText(value: string | undefined | null, max = MAX_TEXT_FIELD_LENGTH): string {
  if (value == null) return '';
  const trimmed = value.replace(/\r\n/g, '\n');
  if (trimmed.length > max) {
    throw new ValidationError(`Text exceeds the ${max} character limit`);
  }
  return trimmed;
}

export function normalizeTitle(value: string): string {
  const title = value.trim().replace(/\s+/g, ' ');
  if (title.length === 0) throw new ValidationError('Title cannot be empty');
  if (title.length > MAX_TITLE_LENGTH) {
    throw new ValidationError(`Title exceeds the ${MAX_TITLE_LENGTH} character limit`);
  }
  return title;
}

export function normalizeTag(value: string): string {
  return value.trim().replace(/\s+/g, ' ').slice(0, MAX_TAG_LENGTH);
}

/** Display-only casing transform; does not change stored tag values. */
export function formatTagForDisplay(tag: string, mode: TagDisplayCase): string {
  if (mode === 'preserve') return tag;
  if (mode === 'lower') return tag.toLowerCase();
  return tag
    .split(/\s+/)
    .map((word) => (word.length === 0 ? word : word[0]!.toUpperCase() + word.slice(1).toLowerCase()))
    .join(' ');
}

/**
 * Tags are compared case-insensitively for deduplication but stored with the casing the
 * user typed, so "Slow Cooker" and "slow cooker" collapse to whichever came first.
 */
export function normalizeTags(values: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const tag = normalizeTag(raw);
    if (tag.length === 0) continue;
    const key = tag.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(tag);
    if (out.length >= MAX_TAGS_PER_RECIPE) break;
  }
  return out.sort((a, b) => a.localeCompare(b));
}

export function normalizeMinutes(value: number | null | undefined): number | null {
  if (value == null) return null;
  if (!Number.isFinite(value) || value < 0) {
    throw new ValidationError('Duration must be a non-negative number of minutes');
  }
  return Math.round(value);
}

export function normalizeSourceType(value: SourceType | undefined): SourceType {
  if (value == null) return 'none';
  if (value !== 'none' && value !== 'url' && value !== 'text') {
    throw new ValidationError(`Unknown source type: ${String(value)}`);
  }
  return value;
}

export function normalizeNutritionBasis(value: NutritionBasis | undefined | null): NutritionBasis {
  if (value == null) return 'unknown';
  if (value !== 'perServing' && value !== 'perRecipe' && value !== 'unknown') {
    throw new ValidationError(`Unknown nutrition basis: ${String(value)}`);
  }
  return value;
}

const COOK_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Validates and returns a calendar day as YYYY-MM-DD. */
export function normalizeCookDate(value: string): string {
  const date = value.trim();
  if (!COOK_DATE_RE.test(date)) {
    throw new ValidationError('Cook date must be YYYY-MM-DD');
  }
  if (!isRealDay(date)) {
    throw new ValidationError('Cook date is not a valid calendar day');
  }
  return date;
}

/** True for an exact YYYY-MM-DD that names a real calendar day. */
export function isCalendarDay(value: string): boolean {
  return COOK_DATE_RE.test(value) && isRealDay(value);
}

function isRealDay(date: string): boolean {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return (
    parsed.getUTCFullYear() === year &&
    parsed.getUTCMonth() === month - 1 &&
    parsed.getUTCDate() === day
  );
}

/**
 * A day note as stored: one line, trimmed, capped.
 *
 * Truncates rather than throwing, like tags: a note is a short label typed into a search
 * box, and an overlong one is a slip, not something to refuse.
 */
export function normalizeDayNote(value: string): string {
  return value.trim().replace(/\s+/g, ' ').slice(0, MAX_DAY_NOTE_LENGTH).trim();
}

/** The scale worth storing with a cook date: undefined for 1× and anything unusable. */
export function normalizeCookDateScale(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0 || value === 1) {
    return undefined;
  }
  return value;
}

/** Local calendar day for `when`, as YYYY-MM-DD. */
export function localCalendarDay(when: Date = new Date()): string {
  const year = when.getFullYear();
  const month = String(when.getMonth() + 1).padStart(2, '0');
  const day = String(when.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Wall clocks never influence merge outcomes — Yjs uses Lamport clocks for that — but
 * `updatedAt` is displayed, so a device with a badly skewed clock should not be able to
 * pin a recipe to the year 2099. Clamping to the last known server time keeps displayed
 * timestamps monotonic without affecting convergence.
 */
export function clampTimestamp(localNow: number, lastKnownServerTime: number | null): number {
  if (lastKnownServerTime == null) return localNow;
  return Math.max(localNow, lastKnownServerTime);
}
